import clsx from 'clsx';
import React from 'react';

import SubscribeForm from '@components/pages/DualSubscribe/VerticalOffer/SubscribeForm';
import type { Benefit } from '@components/pages/SubscribeTest/interfaces';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, TrackingLocation, click } from '@tracking';
import { OfferOption } from '@utils/backend-api/subscribe';
import { LeadTypes } from '@utils/enums';
import { formatNumber } from '@utils/formatting';
import getSubscriptionPriceBreakdown from '@utils/getSubscriptionPriceBreakdown';
import { renewCancelText } from '@utils/offerHelper';

import BenefitsList from './BenefitsList';

const NAVBAR_HEIGHT = 60;

interface OfferPanelProps {
  benefits: Benefit[];
  benefitsHeader: string;
  benefitsHeaderBold: boolean;
  className?: string;
  isOfferSelected: boolean;
  isMobile: boolean;
  offer: OfferOption;
  presentedOffers: OfferOption[];
  setSelectedOffer: (offer: OfferOption) => void;
}

const OfferPanel = ({
  benefits,
  benefitsHeader,
  benefitsHeaderBold,
  className,
  isOfferSelected,
  isMobile,
  offer,
  presentedOffers,
  setSelectedOffer,
}: OfferPanelProps) => {
  const {
    code,
    isPro,
    currentPriceDisplayOverride,
    currentPrice,
    originalPrice,
    originalPriceDisplayOverride,
    billingFrequencyOverride,
    description,
  } = offer;

  const { currentPriceToDisplay, hasDiscount, originalPriceToDisplay } =
    getSubscriptionPriceBreakdown({
      currentPrice,
      currentPriceDisplayOverride,
      originalPrice,
      originalPriceDisplayOverride,
    });

  const { trackEvent } = useEventTracking({
    location: TrackingLocation.VerticalOffer,
  });

  const handleSelectedOffer = () => {
    setSelectedOffer(offer);
    if (isMobile) {
      const element = document.getElementById(code);
      if (!element) return;
      const y = element.getBoundingClientRect().top + window.scrollY - NAVBAR_HEIGHT;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    trackEvent(click({ clickTarget: ClickTarget.Offer, offerCode: code }));
  };

  const planTypeName = isPro ? 'Pro' : 'Annual';

  return (
    <div
      id={code}
      className={clsx(
        className,
        'relative flex w-full touch-manipulation flex-col border border-t-0 border-current px-24 pb-24 pt-32 sm:w-1/2 sm:max-w-full sm:justify-start sm:p-48',
        {
          'cursor-default bg-grayscale-beige': isOfferSelected,
          'cursor-pointer bg-white opacity-80': !isOfferSelected,
        }
      )}
      onClick={!isOfferSelected ? handleSelectedOffer : undefined}
    >
      <div className="flex flex-col gap-8">
        <h2 className="m-0 font-suisse-works text-headline-sm">
          <span className="text-black">The Information </span>
          <span className={isPro ? 'text-pro-gold' : 'text-primary-magenta'}>{planTypeName}</span>
        </h2>

        <div className="flex flex-wrap items-end gap-16">
          <div className="flex items-center gap-8">
            <span className="font-suisse-screen text-headline-alt-sm font-bold leading-[1.3] text-black">
              ${formatNumber(currentPriceToDisplay)}
            </span>
            {hasDiscount && (
              <span className="font-suisse-screen text-headline-alt-sm leading-[1.3] text-grayscale-gray line-through">
                ${formatNumber(originalPriceToDisplay)}
              </span>
            )}
            {billingFrequencyOverride && (
              <span className="font-suisse-screen text-disclosure-lg leading-[1.7] text-black">
                {billingFrequencyOverride}
              </span>
            )}
          </div>
          <span className="font-suisse-screen text-byline-md leading-[1.7] text-grayscale-gray">
            Billed annually
          </span>
        </div>

        <div className="mt-10 flex flex-col gap-8">
          <p
            className={clsx('m-0 font-suisse-intl text-button-lg text-black', {
              'font-bold': benefitsHeaderBold,
            })}
          >
            {benefitsHeader}
          </p>
          <BenefitsList benefits={benefits} isPro={isPro} />
        </div>
      </div>

      <div className="mt-32 flex flex-col gap-8">
        <SubscribeForm
          buttonText="Subscribe"
          isOfferSelected={isOfferSelected}
          leadType={LeadTypes.SubscribeTPage}
          presentedOfferCodes={presentedOffers.map((o) => o.code)}
          selectedOffer={offer}
        />
        <div className="mt-8 text-pretty text-center font-suisse-screen text-disclosure-sm text-grayscale-charcoal">
          {renewCancelText(description)}
        </div>
      </div>
    </div>
  );
};

export default OfferPanel;
